/* eslint-disable no-bitwise */
import { UntypedFormGroup } from "@angular/forms";
import { CellCoords } from "handsontable";
import _ from "lodash";
import { Cellstyle, HotCell, HotCellText } from "~app/common/components/hot-doc/hotdoc/cell";
import { HotDoc, IHotColumnDefinition, IHotDocSettings } from "~app/common/components/hot-doc/hotdoc/doc";
import { HotRow } from "~app/common/components/hot-doc/hotdoc/row";
import { InputField, InputMode, PromptInput } from "~app/common/components/modal/common/prompt-dialog/prompt-dialog.component";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ProduktmatrisGrid } from "../../produktinstruktioner-details/matris/ProduktmatrisGrid";
import { AbstractPrismatrisCell } from "../../produktinstruktioner-details/matris/cells/abstractPrismatrisCell";
import { AddEditLengthCell } from "../../produktinstruktioner-details/matris/cells/addEditLengthCell";
import { RadprisNumberCell } from "../../produktinstruktioner-details/matris/cells/radprisNumberCell";
import { SummaryCell } from "../../produktinstruktioner-details/matris/cells/summaryCell";
import { IRenderConfig } from "../IRenderConfig";
import { ILangdklass } from "./../IProduktdefinition";
import { ProduktdefinitionHeaderData } from "./../ProduktdefinitionHeaderData";
import { BaseMatrix } from "./BaseMatrix";

export class LangdDiameterMatris extends BaseMatrix {
    constructor(grid: ProduktmatrisGrid,
        form: UntypedFormGroup,
        headerData: ProduktdefinitionHeaderData,
        canEdit: boolean) {
        super(grid, form, headerData, canEdit);
    }

    protected afterOnCellMouseDown(event: MouseEvent, coords: CellCoords, th: any, raskModalService: RaskModalService, hotDoc: HotDoc): void {
        const isColumnRow = coords.row === -1;
        const elementClassNames = (event.target as Element)?.className;
        if (elementClassNames?.indexOf("column-icon") >= 0 && isColumnRow) {
            if (elementClassNames?.indexOf("fa-trash") >= 0) {
                hotDoc.deselectAllCells();
                raskModalService
                    .confirm(new ModalSettings("Rensa längder och diameterar", "Vill du rensa längder och diametrar?"))
                    .subscribe(x => {
                        if (x === ConfirmDialogResponse.save) {
                            this.grid.clearGrid();
                            hotDoc.rebind();
                        }
                    });
            } else {
                const oldklass = this.grid.diameterklasser[coords.col - 2]; // Ingen fantastisk lösning tyvärr..
                const oldDiameter = oldklass?.startdiameter;
                if (!ObjectUtils.isNullOrUndefined(oldDiameter)) {
                    this.onAddOrEditDiameter(hotDoc, oldDiameter, raskModalService);
                } else {
                    if (elementClassNames?.indexOf("fa-edit") >= 0) {
                        this.onEditAllDiameter(hotDoc, raskModalService);
                    } else {
                        this.onAddOrEditDiameter(hotDoc, oldDiameter, raskModalService);
                    }
                }
            }
        }
    }

    protected readonly getBaseSettings = (hotDoc, raskModalService: RaskModalService, extraSettings?: IHotDocSettings): IHotDocSettings => {
        const mergedSettings = _.merge(
            {
                raskSettings: {
                    afterOnCellMouseDown: (event: MouseEvent, coords: CellCoords, th: any) => this.afterOnCellMouseDown(event, coords, th, raskModalService, hotDoc)
                }
            } as IHotDocSettings, extraSettings || {});

        return this.sharedSettings(hotDoc, raskModalService, mergedSettings);
    };

    protected readonly buildColumnDefinitions = (editableCollSettings: { readOnly: boolean; type: string; editor?: boolean }): IHotColumnDefinition[] => {
        const columns: IHotColumnDefinition[] = [];
        this.addEditColumn(columns, true);

        columns.push({
            header: this.getTableHeaderText(),
            width: 70,
            readOnly: true,
            cellName: BaseMatrix.rowHeaderCellName
        });

        this.grid.diameterklasser.forEach(diameterklass => {
            columns.push({
                header: this.getColumnHeaderText(diameterklass.startdiameter.toString(), this.editIcon),
                cellName: this.diameterCellName(diameterklass.startdiameter),
                type: editableCollSettings.type,
                readOnly: editableCollSettings.readOnly,
                editor: editableCollSettings.editor
            });
        });

        columns.push({
            header: this.getColumnHeaderEditText(""),
            width: 100,
            readOnly: true,
            cellName: `summa`
        });

        return columns;
    };

    protected readonly onAddOrEditDiameter = (doc: HotDoc, diameter: number, raskModalService: RaskModalService): void => {
        const input = new PromptInput(!diameter ? InputMode.isNew : InputMode.canDelete, {
            inputField: {
                value: diameter,
                label: "Startdiameter",
                type: "number",
                validators: [
                    {
                        expression: (value, oldValue) => !(this.grid.diameterklasser.some(p => p.startdiameter === value) && value !== diameter),
                        message: "Diameterklassen existerar redan"
                    },
                    {
                        expression: (value, oldValue) => value > 0 && value <= this.headerData.maxdiameter,
                        message: `Värde måste vara större än 0 och mindre eller lika som maxdiameter (${this.headerData.maxdiameter ?? "ej tilldelat"})`
                    }
                ]
            }
        });
        input.description = "Ange ett värde som inte redan finns.";

        raskModalService.prompt("Ange diameter", input)
            .subscribe(x => {
                if (!x.cancel) {
                    this.handleGridAdjustment(doc, input, x.fields?.inputField, x, this.grid.deleteColumn, this.grid.addColumn, this.grid.updateColumn);
                }
            }
            );
    };

    protected readonly onEditAllDiameter = (doc: HotDoc, raskModalService: RaskModalService): void => {
        const availableDiameters = this.grid.diameterklasser.map(x => x.startdiameter);
        const editAllMap: { [id: string]: InputField } = {};
        availableDiameters.forEach(x => (
            editAllMap[x] = {
                value: x,
                type: "number",
                columnClass: "col-6",
                validators: [
                    {
                        expression: (value) => value > 0,
                        message: "Diameterklassen måste vara större än 0"
                    }
                ]
            } as InputField));

        const input = new PromptInput(InputMode.none, editAllMap);
        input.onValidate = (values) => this.isRowsUnique(values, `Alla diameterar är inte unika`);
        raskModalService.prompt("Ange diametrar", input)
            .subscribe(x => {
                if (!x.cancel) {
                    let dirty = false;
                    this.grid.diameterklasser.forEach(y => {
                        if (!y.startdiameter !== x.fields[y.startdiameter].value) {
                            this.grid.updateColumn(y.startdiameter, x.fields[y.startdiameter].value);
                            dirty = true;
                        }
                    });
                    if (dirty) {
                        this.form.markAsDirty();
                        doc.rebind();
                    }
                }
            });
    };

    protected readonly onAddOrEditLangd = (doc: HotDoc, langdklass: ILangdklass, raskModalService: RaskModalService): void => {
        const input = new PromptInput(langdklass === null ? InputMode.isNew : InputMode.canDelete, {
            inputField: {
                value: langdklass?.startlangd,
                label: "Startlängd",
                type: "number",
                columnClass: "col-12",
                validators: [
                    {
                        expression: (value, oldValue) => !(this.grid.langdklasser.some(p => p.startlangd === value) && value !== langdklass?.startlangd),
                        message: "Längdklassen existerar redan"
                    },
                    {
                        expression: (value, oldValue) => value > 0 && value <= this.headerData.maxlangd,
                        message: `Värde måste vara större än 0 och mindre eller lika som maxlängd (${this.headerData.maxlangd ?? "ej tilldelat"})`
                    }
                ]
            },
            overmalField: {
                value: langdklass?.overmal ?? 0,
                label: "Övermål",
                type: "number",
                validators: [
                    {
                        expression: (value) => value >= 0,
                        message: "Värde får ej vara negativt"
                    }
                ]
            }
        });

        input.description = "Ange ett värde som inte redan finns.";

        raskModalService.prompt("Ange längd", input)
            .subscribe(x => {
                if (!x.cancel) {
                    const startlangd = this.handleGridAdjustment(doc, input, x.fields?.inputField, x, this.grid.deleteRow, this.grid.addRow, this.grid.updateRow);
                    if (!startlangd) {
                        return;
                    }

                    if (langdklass && langdklass.overmal !== x.fields.overmalField.value) {
                        langdklass.overmal = x.fields.overmalField.value;
                        this.form.markAsDirty();
                        doc.rebind();
                    } else if (langdklass === null) {
                        // Added
                        this.form.markAsDirty();
                        doc.rebind();
                    }
                }
            });
    };

    protected readonly onEditAllLangder = (doc: HotDoc, langdklass: ILangdklass, raskModalService: RaskModalService): void => {
        const editAllMap: { [id: string]: InputField } = {};
        this.grid.langdklasser.forEach(x => (
            editAllMap[x.startlangd] = {
                label: "",
                value: x.startlangd,
                type: "number",
                columnClass: "col-6",
                validators: [
                    {
                        expression: (value) => value > 0,
                        message: "Längdklassen måste vara större än 0"
                    }
                ]
            } as InputField));
        const input = new PromptInput(InputMode.canDelete, editAllMap);

        input.onValidate = (values) => this.isRowsUnique(values, `Alla längder är inte unika`);
        raskModalService.prompt("Ange längder", input)
            .subscribe(x => {
                if (!x.cancel) {
                    let dirty = false;
                    this.grid.langdklasser.forEach(y => {
                        if (!y.startlangd !== x.fields[y.startlangd].value) {
                            this.grid.updateRow(y.startlangd, x.fields[y.startlangd].value);
                            dirty = true;
                        }
                    });
                    if (dirty) {
                        this.form.markAsDirty();
                        doc.rebind();
                    }
                }
            });
    };

    protected buildEmptyRow<T extends AbstractPrismatrisCell>(row: HotRow, doc: HotDoc, renderConfig: IRenderConfig<T>, raskModalService: RaskModalService) {
        row.addText(BaseMatrix.rowHeaderCellName, "", Cellstyle.Readonly);

        this.grid.diameterklasser.forEach(p => {
            row.add(this.diameterCellName(p.startdiameter), new HotCellText("", Cellstyle.Readonly, null));
        });
    }

    protected buildSummaryRow<T extends AbstractPrismatrisCell>(row: HotRow, doc: HotDoc, renderConfig: IRenderConfig<T>, raskModalService: RaskModalService) {
        if (this.canEdit) {
            row.add(BaseMatrix.settingsCellName, this.getEditAllLangdklassCell(doc, null, raskModalService));
            row.add(BaseMatrix.rowHeaderCellName, this.getEditLangdklassCell(doc, null, raskModalService, BaseMatrix.addIcon));
        } else {
            row.addText(BaseMatrix.rowHeaderCellName, "", Cellstyle.Readonly);
        }

        this.grid.diameterklasser.forEach(p => {
            const cells = doc.rows
                .map(x => x.cells[this.diameterCellName(p.startdiameter)] as T);
            row.add(this.diameterCellName(p.startdiameter), renderConfig.columnSummaryFactory(cells));
        });
        const sumOfSums = renderConfig.sumOfSumsFactory(doc.rows.map(p => p.cells[p.cells.length - 1] as SummaryCell));

        row.add("summa", sumOfSums);
    }

    protected buildLangdklassRow<T extends AbstractPrismatrisCell>(doc: HotDoc,
        langdklass: ILangdklass,
        row: HotRow,
        renderConfig: IRenderConfig<T>,
        raskModalService: RaskModalService) {
        if (this.canEdit) {
            row.add(BaseMatrix.settingsCellName, this.getEditLangdklassCell(doc, langdklass, raskModalService));
        }

        row.add(BaseMatrix.rowHeaderCellName, this.getLangdklassHeaderCell(langdklass));

        const cells = [];
        this.grid.diameterklasser.forEach(diameterklass => {
            const pmData = this.grid.getCell(langdklass.startlangd, diameterklass.startdiameter);
            const cell = renderConfig.cellFactory(pmData);
            if (!this.canEdit) {
                cell.setReadOnly();
            }
            row.add(this.diameterCellName(pmData.startdiameter), cell);
            cells.push(cell);
        });

        row.add(`summa`, renderConfig.rowSummaryFactory(cells));
    }

    protected getEditLangdklassCell(doc: HotDoc, langdklass: ILangdklass, raskModalService: RaskModalService, icon = this.editIcon) {
        return new AddEditLengthCell(
            Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
            { rowSpan: 1 },
            icon,
            () => this.onAddOrEditLangd(doc, langdklass, raskModalService));
    }

    protected getEditAllLangdklassCell(doc: HotDoc, langdklass: ILangdklass, raskModalService: RaskModalService, icon = this.editIcon) {
        return new AddEditLengthCell(
            Cellstyle.Readonly | Cellstyle.AlignLeft | Cellstyle.VerticalAlignMiddle,
            { rowSpan: 1 },
            icon,
            () => this.onEditAllLangder(doc, langdklass, raskModalService));
    }

    protected readonly buildRowDefinitions = <T extends AbstractPrismatrisCell>(doc: HotDoc,
        renderConfig: IRenderConfig<T>,
        raskModalService: RaskModalService,
        showPrisrad: boolean): void => {
        this.grid.langdklasser.forEach((langdklass) => {
            doc.createRow((row) => this.buildLangdklassRow(doc, langdklass, row, renderConfig, raskModalService));
        });
        doc.createRow(row => this.buildSummaryRow(row, doc, renderConfig, raskModalService));

        if (showPrisrad) {
            doc.createRow(row => this.buildEmptyRow(row, doc, renderConfig, raskModalService));
            doc.createRow(row => this.buildPrisradRow(row, doc, renderConfig, raskModalService));
        }
    };

    protected getCellsOfType<T extends HotCell>(cells: any[], hotDoc: HotDoc, filterType: new (...params: any[]) => T): T[] {
        const transformedCells = cells.map(c => ({
            row: c[0],
            col: c[1],
            cell: hotDoc.rows[c[0]].cells[c[1]]
        }));
        return transformedCells.filter(f => f.cell instanceof filterType).map(c => (c.cell as T));
    }

    private buildPrisradRow<T extends AbstractPrismatrisCell>(row: HotRow, doc: HotDoc, renderConfig: IRenderConfig<T>, raskModalService: RaskModalService) {
        if (this.canEdit) {
            row.addText(BaseMatrix.settingsCellName, "", Cellstyle.Readonly);
        }

        row.addText(BaseMatrix.rowHeaderCellName, "Radpris", Cellstyle.Readonly);

        this.grid.diameterklasser.forEach(p => {
            row.add(this.diameterCellName(p.startdiameter),
                new RadprisNumberCell(p,
                    this.form,
                    p.radpris,
                    Cellstyle.Default | Cellstyle.AlignCenter,
                    val => p.radpris = val.newValue));
        });
        const sumOfSums = renderConfig.sumOfSumsFactory(doc.rows.map(p => p.cells[p.cells.length - 1] as SummaryCell));

        row.add("summa", sumOfSums);
    }
}
